/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo } from 'react';
import { Container, Box, Typography } from '@mui/material';
import {
  useApiClient,
  useFetch,
  InfiniteScroll,
} from '@marageti/z4-lib';
import { Destination, TripRequestStatus } from '@marageti/z4-sdk/lib/travel';
import { TeamMember } from '@marageti/z4-sdk/lib/people';
import { SearchResult } from '@marageti/z4-sdk/lib/entity';
import TripRequestCard from '../../../components/list-cards/trip-request-cards/all-card';
import AllFilters from './all-filters';
import useTripRequestSearch from '../../../hooks/use-trip-request-search';
import useURLSearchQuery from '../../../hooks/use-url-for-search-query';
import type { TripSearchDateRangeKey } from '../../../components/trip-search-date-range-filters/trip-search-date-range-filters';
import {
  orderByOptions,
  sortByOptionsAll as sortByOptions,
  tagOptions,
  statusChips,
  Option,
  ANY_ASSIGNEE,
  AssigneeOption,
  teamMemberSearchRequest,
  formatTeamMembersAsOptions,
} from '../options';

interface DefaultSearch {
  [key: string]: string | string[];
  assignee: string;
  destinations: string[];
  departureDateFrom: string;
  departureDateTo: string;
  fullText: string;
  matchDateFrom: string;
  matchDateTo: string;
  order: string;
  sort: string;
  status: string[];
  tag: string;
}

const defaultSearch: DefaultSearch = {
  assignee: ANY_ASSIGNEE.value,
  destinations: [],
  departureDateFrom: '',
  departureDateTo: '',
  fullText: '',
  matchDateFrom: '',
  matchDateTo: '',
  order: orderByOptions[0].value,
  sort: sortByOptions[0].value,
  status: [TripRequestStatus.PendingStatus, TripRequestStatus.VerifiedStatus, TripRequestStatus.MatchedStatus, TripRequestStatus.SoldStatus],
  tag: 'ALL',
};

const TripsView = () => {
  // Fetch all needed data
  const apiClient = useApiClient();
  const { data: teamMembers } = useFetch<SearchResult<TeamMember>>(apiClient.peopleClient.postTeamMemberSearch, [teamMemberSearchRequest], true);
  const { data: destinationsList, isLoading: isLoadingDestinations } = useFetch<Destination[]>(apiClient.destinationsClient.getDestinations, [{ params: { limit: 1000, all: true } }], true);

  const [queryValues, updateURLQuery] = useURLSearchQuery<DefaultSearch>(defaultSearch);
  const {
    sort,
    order,
    fullText,
    assignee,
    destinations,
    tag,
    status,
    departureDateFrom,
    departureDateTo,
    matchDateFrom,
    matchDateTo,
  } = queryValues;

  const {
    tripRequests,
    error,
    hasMore,
    isLoading,
    loadMore,
    totalHits,
  } = useTripRequestSearch(queryValues);
  // Format options for Assignee Filter
  const teamMembersAsOptions = useMemo(
    () => formatTeamMembersAsOptions(teamMembers),
    [teamMembers],
  );

  // Format options for Destinations Filter
  const destinationsAsOptions = useMemo(() => destinationsList?.map((destination) => ({
    title: destination.name,
    value: destination.id,
  })).sort((a, b) => a.title.localeCompare(b.title)), [destinationsList]) || [];

  // Format options for Order By Filter
  const filteredOrderByOptions = useMemo(() => orderByOptions.filter((option) => option.field === sort), [sort, orderByOptions]);

  // Get selected assignee option from assignee
  const assigneeName = teamMembersAsOptions?.find((tm) => tm.value === assignee)?.title || '';
  const selectedAssignee: Option = teamMembers && assignee ? { title: assigneeName, value: assignee } : ANY_ASSIGNEE;
  // Get selected destination options from destinations
  const selectedDestinations = destinations.length > 0 && destinationsAsOptions.length > 0
    ? destinationsAsOptions.filter((d) => destinations.includes(d.value)) : [];

  const onOrderSortChange = (name: string, v: string | boolean) => {
    if (name === 'sort') {
      const newSortBy = sortByOptions.find((o) => o.value === v) || sortByOptions[0];
      const defaultOrderBy = orderByOptions.find((o) => o.field === newSortBy.value);
      const param = {
        sort: newSortBy.value,
        order,
      };
      if (defaultOrderBy) {
        param.order = defaultOrderBy.value;
      }
      updateURLQuery(param);
    } else {
      // Make sure you are selecting the orderBy option that corresponds with the currently selected sort field.
      const newOrderBy = orderByOptions.find((o) => o.field === sort && o.value === v) || orderByOptions[0];
      updateURLQuery({ order: newOrderBy.value });
    }
  };

  const onDestinationChange = (newValue: Option[]) => {
    updateURLQuery({ destinations: newValue.map((n) => n.value) });
  };

  const onAssigneeChange = (newValue: AssigneeOption | null) => {
    updateURLQuery({ assignee: newValue?.value || ANY_ASSIGNEE.value });
  };

  const onSearchChange = (newValue: string) => {
    updateURLQuery({ fullText: newValue });
  };

  const onTagChange = (selectedTag: string) => {
    updateURLQuery({ tag: selectedTag });
  };

  const onChipChange = (selectedChips: string[]) => {
    updateURLQuery({ status: selectedChips });
  };

  const onDateFilterChange = (name: TripSearchDateRangeKey, value: string) => {
    updateURLQuery({ [name]: value });
  };

  const formattedTrCount = new Intl.NumberFormat('en-US').format(totalHits);

  return (
    <Container maxWidth="xl">
      <Box pt={6} pb={12}>
        <Typography variant="h2" component="h1" mb={2}>
          All Requests
        </Typography>
        <AllFilters
          // Search Props
          searchValue={fullText}
          onSearchChange={onSearchChange}
          // Destination Props
          isLoadingDestinations={isLoadingDestinations}
          selectedDestinations={selectedDestinations}
          destinationOptions={destinationsAsOptions}
          onDestinationChange={onDestinationChange}
          // Assignee Props
          assignees={teamMembersAsOptions}
          selectedAssignee={selectedAssignee}
          onAssigneeChange={onAssigneeChange}
          // Sort and Order Props
          orderBy={order}
          orderByOptions={filteredOrderByOptions}
          sortBy={sort}
          sortByOptions={sortByOptions}
          onOrderSortChange={onOrderSortChange}
          // Chip Props
          onTagChange={onTagChange}
          tagOptions={tagOptions}
          selectedTag={tag}
          statusChips={statusChips}
          selectedStatusChips={status}
          onStatusChipChange={onChipChange}
          departureDateFrom={departureDateFrom}
          departureDateTo={departureDateTo}
          matchDateFrom={matchDateFrom}
          matchDateTo={matchDateTo}
          onDateFilterChange={onDateFilterChange}
        />
        {!!(tripRequests) && (
          <Typography variant="h4" sx={{ mb: 2 }}>
            {`${totalHits === 10000 ? `${formattedTrCount}+` : formattedTrCount} Results`}
          </Typography>
        )}
        <InfiniteScroll
          error={error}
          hasMoreItems={hasMore}
          isLoading={isLoading}
          loadMoreItems={loadMore}
          stackProps={{ spacing: 1 }}
        >
          {tripRequests?.map((tripRequest) => (
            <TripRequestCard
              key={tripRequest.id}
              tripRequest={tripRequest}
            />
          ))}
        </InfiniteScroll>
      </Box>
    </Container>
  );
};

export default TripsView;
